import { NativeButton } from '@/components/common/NativeButton';
import { usePhotoPicker, releasePhotoAsset } from '@/components/photos/usePhotoPicker';
import type { UploadableImageAsset } from '@/lib/image-upload';
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import { Camera, Check, Loader2, Plus } from "lucide-react";
import { colors } from "@tabbio/tokens";
import { USERNAME_MAX_LENGTH } from "@tabbio/utils/username-policy";
import { useToast } from "../../hooks/useToast";
import { useAuth } from "../../hooks/useAuth";
import { useUaePassStatus } from "../../hooks/useUaePassStatus";
import {
  useCheckUsername,
  useSaveProfileSetup,
} from "../../hooks/useOnboardingApi";
import { mergeAuthenticatedUser } from "../../lib/app-auth";
import { useCVData } from "../../lib/cv-mock-data";
import { revealUploadedCvPhoto } from "../../lib/cv-photo-state";
import { uploadImageAsset } from "../../lib/image-upload";
import { getPublicSiteLabel } from "../../lib/public-web";
import { TabbioMark } from "../common/TabbioMark";
import { WebInput, WebSaveButton } from "../cv/forms/web-form-elements";
import { BasicsInfoDialog, type BasicsInfoDialogRow } from "./BasicsInfoDialog";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingFooterNote, OnboardingWebStep } from "./web-step-chrome";
import { resolveUaePassCvName } from "../../lib/uae-pass-names";

function toHandleBase(name: string): string {
  return name
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[^a-z]+/, "")
    .replace(/[^a-z0-9]+$/, "")
    .slice(0, USERNAME_MAX_LENGTH)
    .replace(/[^a-z0-9]+$/, "");
}

// Light normalization applied as the user types (see the native twin for the
// rationale): what we check is what we save.
function normalizeHandleInput(value: string): string {
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]+/g, "")
    .replace(/-{2,}/g, "-");
}

export function StepThisIsYou() {
  const { t } = useTranslation("auth");
  const toast = useToast();
  const { user, isUaePassVerified } = useAuth();
  const { data: uaePassStatus } = useUaePassStatus(isUaePassVerified);
  const syncedProfileRef = useRef(false);
  const {
    profileData,
    setProfileData,
    hasCompletedProfileSetup,
    nextStep,
    prevStep,
    photoVisibility,
    setPhotoVisibility,
  } = useOnboarding();
  const { updateProfile, updateCVConfig } = useCVData();
  const saveProfileMutation = useSaveProfileSetup();

  const [name, setName] = useState(profileData.name);
  const [avatarUrl, setAvatarUrl] = useState(profileData.avatarUrl);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const { pickPhoto, cropper, isPicking } = usePhotoPicker({ kind: 'cv' });
  const photoGeneration = useRef(0);
  useEffect(() => () => { photoGeneration.current += 1; }, []);
  const [infoOpen, setInfoOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [hasSeeded, setHasSeeded] = useState(false);
  const [linkFocused, setLinkFocused] = useState(false);
  const cvNameTouchedRef = useRef(false);
  const cvNameSeed = resolveUaePassCvName(
    uaePassStatus?.uaePassData,
    user?.name || "",
  );

  useEffect(() => {
    if (syncedProfileRef.current) {
      return;
    }
    if (!profileData.name && !profileData.avatarUrl) {
      return;
    }

    syncedProfileRef.current = true;
    setName(profileData.name);
    setAvatarUrl(profileData.avatarUrl);
  }, [profileData.avatarUrl, profileData.name]);

  useEffect(() => {
    if (
      !isUaePassVerified ||
      name.trim() ||
      cvNameTouchedRef.current ||
      !cvNameSeed
    ) {
      return;
    }
    setName(cvNameSeed);
  }, [cvNameSeed, isUaePassVerified, name]);

  // Seed the link once from the name (or the account's existing handle). Until
  // the user edits the link directly, later name edits keep the link in sync.
  // Mirrors the native twin.
  useEffect(() => {
    if (hasSeeded || usernameTouched) {
      return;
    }
    // Seed from the loaded profile only: never from live Name keystrokes.
    if (!profileData.name && !cvNameSeed && !profileData.username) {
      return;
    }
    // Returning after the handle was already claimed (next → back, or a resumed
    // session): restore that exact handle instead of re-deriving from the name,
    // and mark it touched so later name edits / auto-suggestions never silently
    // overwrite it (issue #167). Mirrors the native twin.
    if (hasCompletedProfileSetup && profileData.username?.trim()) {
      setUsername(profileData.username.trim());
      setUsernameTouched(true);
      setHasSeeded(true);
      return;
    }
    setUsername(
      toHandleBase(profileData.name || cvNameSeed) ||
        profileData.username?.trim() ||
        "",
    );
    setHasSeeded(true);
  }, [
    hasSeeded,
    usernameTouched,
    cvNameSeed,
    profileData.name,
    profileData.username,
    hasCompletedProfileSetup,
  ]);

  const siteLabel = getPublicSiteLabel() || "tabbio.com";
  const photoPublic = photoVisibility === "public";
  // We live-check the exact field value (name-derived while untouched, the user's
  // text once edited). What we show + save is `effectiveHandle`, which in auto
  // mode may swap in the server's suggestion (see native twin for the rationale).
  const handle = username;
  const { isAvailable, suggestion, reason, isChecking, checkedUsername } =
    useCheckUsername(handle);
  const checkSettled = checkedUsername === handle && !isChecking;
  // Auto mode (untouched, name-derived): silently adopt the server's available
  // suggestion when the handle is taken/reserved; manual edits are never rewritten.
  const autoAdoptSuggestion =
    !usernameTouched && checkSettled && isAvailable === false && !!suggestion;
  const effectiveHandle =
    autoAdoptSuggestion && suggestion ? suggestion : handle;
  const displayValue = effectiveHandle;

  const handleChangeName = (value: string) => {
    cvNameTouchedRef.current = true;
    setName(value);
    if (!usernameTouched) {
      setUsername(toHandleBase(value));
      setHasSeeded(true);
    }
  };

  let linkStatusText: string | null = null;
  let linkStatusColor = "var(--text-tertiary)";
  if (handle.length === 0) {
    if (usernameTouched || hasSeeded) {
      linkStatusText = t("onboarding.username.required");
      linkStatusColor = usernameTouched ? "#d93025" : "var(--text-tertiary)";
    }
  } else if (handle.length < 3) {
    linkStatusText = t("onboarding.username.tooShort");
    linkStatusColor = "#d93025";
  } else if (isChecking || !checkSettled) {
    linkStatusText = t("onboarding.username.checking");
  } else if (isAvailable === true) {
    linkStatusText = t("onboarding.username.available");
    linkStatusColor = "#17803d";
  } else if (isAvailable === false) {
    if (autoAdoptSuggestion) {
      // Auto mode quietly uses the server's available suggestion → claimable.
      linkStatusText = t("onboarding.username.available");
      linkStatusColor = "#17803d";
    } else {
      linkStatusText =
        reason === "FORMAT_INVALID"
          ? t("onboarding.username.invalidFormat")
          : reason === "RESERVED"
            ? t("onboarding.username.reserved")
            : t("onboarding.username.taken");
      linkStatusColor = "#d93025";
    }
  } else {
    // Settled with no verdict → availability check failed; fail open (backend
    // re-validates on save) rather than dead-ending Continue.
    linkStatusText = t("onboarding.username.checkFailed");
    linkStatusColor = "var(--text-tertiary)";
  }

  // Chip only when the user typed their own handle and it clashes; auto mode
  // resolves itself via autoAdoptSuggestion.
  const showSuggestionChip =
    usernameTouched && checkSettled && isAvailable === false && !!suggestion;

  const linkReady =
    handle.length >= 3 &&
    checkSettled &&
    // `isAvailable === null` here means the check failed: fail open.
    (isAvailable === true || autoAdoptSuggestion || isAvailable === null);

  const handleChangeUsername = (value: string) => {
    setUsernameTouched(true);
    setUsername(normalizeHandleInput(value));
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setUsernameTouched(true);
    setUsername(suggestion);
  };

  const handleFileChange = async () => {
    const generation = photoGeneration.current;
    let asset: UploadableImageAsset | null = null;
    try {
      asset = await pickPhoto();
      if (!asset || generation !== photoGeneration.current) return;
      setIsUploadingAvatar(true);
      const uploadedUrl = await uploadImageAsset(asset, {
        kind: "profile-avatar",
        fileName: asset.name,
        mimeType: asset.type,
      });
      if (generation !== photoGeneration.current) return;
      setAvatarUrl(uploadedUrl);
      setProfileData({ avatarUrl: uploadedUrl });
      revealUploadedCvPhoto(uploadedUrl, { updateProfile, updateCVConfig });
    } catch (error) {
      toast.error(
        t("onboarding.profileSetup.photoUploadFailed", {
          defaultValue: "Could not upload your photo",
        }),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      if (asset) void releasePhotoAsset(asset);
      if (generation === photoGeneration.current) setIsUploadingAvatar(false);
    }
  };

  const handleContinue = async () => {
    const trimmed = name.trim();
    if (
      !trimmed ||
      isUploadingAvatar || isPicking ||
      isChecking ||
      saveProfileMutation.isPending ||
      !linkReady
    ) {
      return;
    }

    // The public handle is CV/profile data, not UAE PASS legal identity.
    const usernameToSave = effectiveHandle;

    try {
      const result = await saveProfileMutation.mutateAsync({
        name: trimmed,
        username: usernameToSave,
        avatarUrl: avatarUrl || null,
      });
      // Trust only the server-confirmed handle. Never fall back to the value the
      // user typed (`usernameToSave`): a claim that silently returned no handle
      // must not leave an unowned handle in the preview URL: that is issue #84,
      // where a losing concurrent session displayed a handle another user owned.
      const savedUsername = result.username ?? profileData.username;
      const savedName = trimmed;
      setProfileData({ name: savedName, avatarUrl, username: savedUsername });
      updateProfile({ name: savedName, avatarUrl });
      // Keep the auth session's handle in lockstep with what was just claimed so
      // every post-claim consumer (share card, settings, the /complete preview)
      // reads one server-confirmed value, not a stale seeded username.
      if (result.username) {
        mergeAuthenticatedUser({ username: result.username });
      }
      void nextStep();
    } catch (error) {
      toast.error(
        t("onboarding.thisIsYou.linkSaveFailed", {
          defaultValue: "Could not reserve your link",
        }),
        error instanceof Error ? error.message : undefined,
      );
    }
  };

  const infoRows: BasicsInfoDialogRow[] = [
    {
      question: t("onboarding.howToReachYou.modalRow1Q"),
      answer: t("onboarding.howToReachYou.modalRow1A"),
    },
    {
      question: t("onboarding.howToReachYou.modalRow2Q"),
      answer: t("onboarding.howToReachYou.modalRow2A"),
    },
    {
      question: t("onboarding.howToReachYou.modalRow3Q"),
      answer: t("onboarding.howToReachYou.modalRow3A"),
    },
  ];

  const disabled =
    !name.trim() ||
    isUploadingAvatar || isPicking ||
    isChecking ||
    saveProfileMutation.isPending ||
    !linkReady;

  return (
    <OnboardingWebStep
      titleBase={t("onboarding.thisIsYou.titleBase", {
        defaultValue: "This is",
      })}
      titleAccent={t("onboarding.thisIsYou.titleAccent", {
        defaultValue: "you",
      })}
      subtitle={t("onboarding.thisIsYou.subtitle", {
        defaultValue: "Your name, photo and Tabbio link",
      })}
      onBack={prevStep}
    >
      {cropper}
      {/* Photo + visibility card */}
      <section style={photoBlockStyle}>
        <div style={photoRowStyle}>
          <button
            type="button"
            onClick={() => void handleFileChange()}
            disabled={isUploadingAvatar || isPicking}
            style={photoButtonStyle}
            aria-label={t("onboarding.thisIsYou.photoLabel")}
          >
            {/* The circle clips the avatar; the + badge sits OUTSIDE it on the
                button so the circle's overflow:hidden can't cut it off. */}
            <span style={photoCircleStyle}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={photoImageStyle} />
              ) : (
                <Camera size={22} color="var(--text-tertiary)" />
              )}
            </span>
            <span style={photoBadgeStyle}>
              {isUploadingAvatar ? (
                <Loader2
                  size={13}
                  color="#ffffff"
                  style={{ animation: "spin 1s linear infinite" }}
                />
              ) : (
                <Plus size={13} color="#ffffff" strokeWidth={3} />
              )}
            </span>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={photoTitleStyle}>
              {t("onboarding.thisIsYou.photoLabel")}
            </div>
            <div style={helperTextStyle}>
              {t("onboarding.thisIsYou.photoSublabel")}
            </div>
          </div>
        </div>

{avatarUrl ? <NativeButton title={t('common:accountPhoto.removeCv')} variant="ghost" color="danger" disabled={isUploadingAvatar || isPicking}
        onPress={() => { setAvatarUrl(''); setProfileData({ avatarUrl: '' }); updateProfile({ avatarUrl: '' }); updateCVConfig({ showPhoto: false }); }} /> : null}
        <div style={photoDividerStyle} />
        <div style={photoToggleRowStyle}>
          <span style={photoToggleLabelStyle}>
            {t("onboarding.thisIsYou.hidePhotoPublic")}
          </span>
          <PhotoVisibilitySwitch
            value={!photoPublic}
            onChange={(next) =>
              setPhotoVisibility(next ? "employers" : "public")
            }
            label={t("onboarding.thisIsYou.hidePhotoPublic")}
          />
        </div>
      </section>

      {/* Editable name rendered on the CV and public profile. */}
      <WebInput
        label={
          isUaePassVerified
            ? t("onboarding.thisIsYou.cvNameLabel")
            : t("onboarding.thisIsYou.fullNameLabel")
        }
        value={name}
        onChange={handleChangeName}
        placeholder={t("onboarding.personalInfo.namePlaceholder", {
          defaultValue: "Ahmed Ali Omar",
        })}
        autoComplete="name"
        readOnly={false}
      />

      {/* Tabbio link: pill field matching INPUT_BASE with a leading mark and
          a static .site suffix */}
      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>
          {t("onboarding.thisIsYou.tabbioLinkLabel")}
        </label>
        <div
          style={{
            ...linkPillStyle,
            borderColor: linkFocused
              ? "var(--text-primary)"
              : "var(--input-border)",
            opacity: 1,
          }}
        >
          <TabbioMark size={16} color={colors.primary} />
          <input
            value={displayValue}
            onChange={(event) => handleChangeUsername(event.target.value)}
            onFocus={() => setLinkFocused(true)}
            onBlur={() => setLinkFocused(false)}
            placeholder={t("onboarding.thisIsYou.tabbioLinkPlaceholder", {
              defaultValue: "your-link",
            })}
            maxLength={USERNAME_MAX_LENGTH}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            readOnly={false}
            aria-readonly={false}
            style={linkInputStyle}
          />
          <span style={linkSuffixStyle}>.{siteLabel}</span>
        </div>
        {linkStatusText ? (
          <span style={{ ...statusTextStyle, color: linkStatusColor }}>
            {linkStatusText}
          </span>
        ) : null}
        {showSuggestionChip && suggestion ? (
          <button
            type="button"
            onClick={applySuggestion}
            aria-label={t("onboarding.username.useSuggestion", { suggestion })}
            style={suggestChipStyle}
          >
            <Check size={13} color={colors.primary} />
            <span style={suggestChipTextStyle}>{suggestion}</span>
          </button>
        ) : null}
      </div>

      <OnboardingFooterNote
        text={t("onboarding.thisIsYou.privacyFooter")}
        linkLabel={t("onboarding.thisIsYou.privacyLink")}
        onLinkClick={() => setInfoOpen(true)}
      />
      <WebSaveButton
        label={t("onboarding.thisIsYou.cta")}
        onClick={handleContinue}
        loading={saveProfileMutation.isPending}
        disabled={disabled}
      />

      <BasicsInfoDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t("onboarding.howToReachYou.modalTitle")}
        doneLabel={t("onboarding.howToReachYou.modalDone")}
        rows={infoRows}
      />
    </OnboardingWebStep>
  );
}

/**
 * Compact animated switch: the track/thumb recipe from `WebToggle`
 * (web-form-elements.tsx) without its full-width row chrome, so it fits inside
 * the photo card. Positions via inset-inline so RTL mirrors correctly.
 */
function PhotoVisibilitySwitch({
  value,
  onChange,
  label,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={label}
      onClick={() => onChange(!value)}
      style={{
        width: 48,
        height: 30,
        borderRadius: 15,
        backgroundColor: value ? "var(--text-primary)" : "rgba(0,0,0,0.06)",
        border: value ? "none" : "1px solid var(--input-border)",
        position: "relative",
        transition: "background-color 200ms, border-color 200ms",
        flexShrink: 0,
        cursor: "pointer",
        padding: 0,
      }}
    >
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 12,
          // Issue #133: was `var(--bg)`, which is near-black in dark mode and
          // made the thumb disappear against the track.
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          position: "absolute",
          top: 3,
          insetInlineStart: value ? 21 : 3,
          transition: "inset-inline-start 200ms",
        }}
      />
    </button>
  );
}

const photoBlockStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 14,
  borderRadius: 18,
  backgroundColor: "rgba(0,0,0,0.04)",
  marginTop: 4,
};

const photoRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
};

const photoButtonStyle: CSSProperties = {
  position: "relative",
  width: 72,
  height: 72,
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  flexShrink: 0,
};

const photoCircleStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  border: "1px solid var(--input-border)",
  borderRadius: "50%",
  backgroundColor: "var(--input-bg)",
  boxSizing: "border-box",
};

const photoImageStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const photoBadgeStyle: CSSProperties = {
  position: "absolute",
  insetInlineEnd: 2,
  bottom: 2,
  width: 22,
  height: 22,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: colors.primary,
  border: "2px solid var(--input-bg)",
};

const photoTitleStyle: CSSProperties = {
  color: "var(--text-primary)",
  fontSize: 15,
  fontWeight: 500,
};

const helperTextStyle: CSSProperties = {
  marginTop: 2,
  color: "var(--text-tertiary)",
  fontSize: 13,
  lineHeight: "18px",
};

const photoToggleRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const photoDividerStyle: CSSProperties = {
  width: "100%",
  height: 1,
  backgroundColor: "var(--input-border)",
};

const photoToggleLabelStyle: CSSProperties = {
  color: "var(--text-primary)",
  fontSize: 13,
  fontWeight: 500,
};

const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: 8,
  display: "block",
  fontFamily: "inherit",
};

const linkPillStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  height: 52,
  borderRadius: 9999,
  borderWidth: 1,
  borderStyle: "solid",
  padding: "0 22px",
  backgroundColor: "var(--input-bg)",
  boxShadow: "var(--gem-input-shadow)",
  boxSizing: "border-box",
  transition: "border-color 150ms",
};

const linkInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 0,
  outline: 0,
  padding: 0,
  color: "var(--text-primary)",
  background: "transparent",
  fontFamily: "inherit",
  fontSize: 15,
  direction: "ltr",
};

const linkSuffixStyle: CSSProperties = {
  color: "var(--text-tertiary)",
  fontSize: 15,
  flexShrink: 0,
};

const statusTextStyle: CSSProperties = {
  fontSize: 12,
  lineHeight: "16px",
  marginTop: 6,
  paddingInline: 4,
  display: "block",
};

const suggestChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  marginTop: 8,
  marginInlineStart: 4,
  padding: "5px 12px",
  borderRadius: 9999,
  border: `1px solid ${colors.primary}33`,
  backgroundColor: `${colors.primary}14`,
  color: colors.primary,
  cursor: "pointer",
  fontFamily: "inherit",
  maxWidth: "100%",
};

const suggestChipTextStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
